import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import { formatIsoDateTimeByTimeZone } from "~/utils/dateTimeUtils";
import {
  type ActivationCheckResponse,
  type AppSettingsResponse,
  fetchAndSavePhoneBooksEntity,
  getActivationData,
  getAppSettingAutoUpdateData,
  getPhoneBookAutoUpdateData,
  getAppSettingsData,
  getPhoneBooksEntity,
  type PhoneBooksData,
  restoreActivationDataIfMissing,
  saveAppSettingAutoUpdateData,
  saveAppSettingsData,
  savePhoneBookAutoUpdateData,
  touchAppSettingAutoUpdateNow,
  touchPhoneBookAutoUpdateNow,
} from "~/utils/receptionDB";
import { syncActivationDataFromCheck, syncPhoneBooksData } from "~/utils/receptionUtils";

const activationCheckUrl = buildIfaceApiUrl("v1/activation/check");
const phoneBooksUrl = buildIfaceApiUrl("v1/phone-books");
const appSettingsUrl = buildIfaceApiUrl("v1/app-settings");

/** アクティベーションチェック間隔（ミリ秒） */
const ACTIVATION_CHECK_INTERVAL = 60_000; // 1分

/** 統合ステータス */
export type ActivationDataStatus = "idle" | "loading" | "success" | "error";

type ActivationCheckMode = "initial" | "polling";

type ReceptionModalErrorCode = 102 | 103;

const getReceptionModalMessageKey = (
  errorCode: number | null
): `receptionErrorModal.${ReceptionModalErrorCode}` | null => {
  if (errorCode === 102 || errorCode === 103) {
    return `receptionErrorModal.${errorCode}`;
  }

  return null;
};

/** フック戻り値 */
export interface UseReceptionStandbyResult {
  /** API通信中フラグ */
  isLoading: boolean;
  /** アクティベーション有効期限切れフラグ */
  isExpired: boolean;
  /** activation/check のエラーコード */
  activationErrorCode: number | null;
  /** エラーメッセージ */
  errorMessage: string | null;
  /** 受付エラーモーダルに表示するメッセージ */
  receptionErrorMessage: string | null;
  /** アクティベーション情報 */
  activationInfo: ActivationCheckResponse | null;
  /** 画面遷移可能フラグ（API通信中でなく、有効期限切れでもない） */
  canNavigate: boolean;
  /**
   * checkActivation + phoneBook + appSettings の統合ステータス
   * ProgressModal の「activation_and_data」キーに対応
   */
  activationDataStatus: ActivationDataStatus;
  /** 1分間隔のポーリング停止 */
  stopPolling: () => void;
  /** 電話帳メタデータ（useReceptionStandby で保持・同期） */
  phoneBookMeta: PhoneBooksData | null;
}

/**
 * 受付待受画面の状態管理フック
 *
 * - 1分ごとにアクティベーションチェックAPIを呼び出す
 * - バックグラウンドで電話帳・APP設定をダウンロード
 * - 同一 phoneBookId / appSettingUpdateDateTime は重複ダウンロードしない
 * - 有効期限切れの場合はエラー表示
 */
export const useReceptionStandby = (): UseReceptionStandbyResult => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [activationErrorCode, setActivationErrorCode] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [receptionErrorMessage, setReceptionErrorMessage] = useState<string | null>(null);
  // 電話帳メタは React のステートで保持し、永続化されると UI を更新する
  const [phoneBookMeta, setPhoneBookMeta] = useState<PhoneBooksData | null>(null);
  const [activationInfo, setActivationInfo] = useState<ActivationCheckResponse | null>(null);

  /** 統合ステータス */
  const [activationDataStatus, setActivationDataStatus] = useState<ActivationDataStatus>("idle");

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activationCheckInFlightRef = useRef<Promise<boolean> | null>(null);
  const performActivationCheckRef = useRef<
    ((mode: ActivationCheckMode) => Promise<boolean>) | null
    // eslint-disable-next-line indent
  >(null);

  const getDownloadedPhoneBookId = async (): Promise<number | null> => {
    try {
      const phoneBooksEntity = await getPhoneBooksEntity();
      return phoneBooksEntity?.phoneBookId ?? null;
    } catch {
      return null;
    }
  };

  const getDownloadedAppSettingDate = async (): Promise<string | null> => {
    try {
      const appSettingAutoUpdate = await getAppSettingAutoUpdateData();

      return appSettingAutoUpdate?.lastUpdateDate ?? null;
    } catch {
      return null;
    }
  };

  const getStoredPhoneBook = async (): Promise<unknown | null> => {
    try {
      return (await getPhoneBooksEntity()) ?? null;
    } catch {
      return null;
    }
  };

  const getStoredPhoneBookMeta = async (): Promise<PhoneBooksData | null> => {
    try {
      const phoneBooksEntity = await getPhoneBooksEntity();
      if (!phoneBooksEntity) return null;

      return {
        phoneBookId: phoneBooksEntity.phoneBookId,
        plan: phoneBooksEntity.plan,
        isSelectCompanyEnabled: phoneBooksEntity.isSelectCompanyEnabled,
        isAppointmentEnabled: phoneBooksEntity.isAppointmentEnabled,
        isFacialIdentificationEnabled: phoneBooksEntity.isFacialIdentificationEnabled,
        isAppointmentHistoryEnabled: phoneBooksEntity.isAppointmentHistoryEnabled,
        responseDataUrl: "",
      };
    } catch {
      return null;
    }
  };

  // マウント時に IndexedDB から `phoneBookMeta` ステートを初期化する
  useEffect(() => {
    let isMounted = true;

    void (async () => {
      try {
        const storedMeta = await getStoredPhoneBookMeta();
        if (isMounted) {
          setPhoneBookMeta(storedMeta);
        }
      } catch {
        // 無視する
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // ------------------------------------------------------------------
  // phoneBook ダウンロード（必要な場合のみ）
  // ------------------------------------------------------------------
  const fetchPhoneBookMeta = async (): Promise<PhoneBooksData> => {
    const res = await apiClient.get<{ data: PhoneBooksData }>(phoneBooksUrl, {
      headers: createIfaceHeaders(true),
    });
    return res.data.data as PhoneBooksData;
  };

  const downloadPhoneBookIfNeeded = useCallback(
    async (activationData: ActivationCheckResponse): Promise<boolean> => {
      const newId = activationData.phoneBookId;
      if (newId === null || newId === undefined) return false;

      try {
        const phoneBooksData = await fetchPhoneBookMeta();
        const responseDataUrl = phoneBooksData.responseDataUrl;
        const phoneBooksEntity = await fetchAndSavePhoneBooksEntity(
          responseDataUrl,
          phoneBooksData,
          false
        );
        if (!phoneBooksEntity) {
          return false;
        }
        await syncPhoneBooksData(phoneBooksEntity);
        setPhoneBookMeta(phoneBooksData);
        return true;
      } catch (err) {
        console.error(`待受画面 電話帳取得API失敗:${(err as Error)?.message ?? String(err)}`);
      }

      return false;
    },
    [fetchPhoneBookMeta, setPhoneBookMeta]
  );

  // ------------------------------------------------------------------
  // appSettings ダウンロード（必要な場合のみ）
  // ------------------------------------------------------------------
  const downloadAppSettingsIfNeeded = useCallback(
    async (activationData: ActivationCheckResponse): Promise<boolean> => {
      const reservationIso = activationData.appSettingUpdateDateTime;
      // 予約時刻（reservation）をパースする。無効な予約時刻の場合は
      // 「重複ダウンロード抑止」の判定だけをスキップし、ダウンロード自体は続行する。
      const reservationMs = reservationIso ? new Date(reservationIso).getTime() : NaN;
      const hasValidReservation = !!reservationIso && !Number.isNaN(reservationMs);

      // 実際の最終ダウンロード時刻（lastUpdateDate）を取得する（存在すれば重複抑止に利用）
      const storedAuto = await getAppSettingAutoUpdateData();
      const storedLastDownloaded = storedAuto?.lastUpdateDate ?? null;

      if (hasValidReservation && storedLastDownloaded) {
        const lastMs = new Date(storedLastDownloaded).getTime();
        if (!Number.isNaN(lastMs) && lastMs >= reservationMs) {
          // 予約時刻以降に既にダウンロード済み -> 抑止
          return false;
        }
      }

      try {
        const appSettingsRes = await apiClient.get<AppSettingsResponse>(appSettingsUrl, {
          headers: createIfaceHeaders(true),
        });
        const settings: AppSettingsResponse = appSettingsRes.data as AppSettingsResponse;
        try {
          await saveAppSettingsData(settings);
          // 同一タブ内のリスナーに appSettings が更新されたことを通知する
          window.dispatchEvent(
            new CustomEvent("reception:appSettings:changed", {
              detail: settings,
            })
          );
        } catch (e) {
          console.error("Failed to write appSettings to IndexedDB:", e);
          return false;
        }
        return true;
      } catch (err) {
        console.error(`待受画面 アプリ設定取得API失敗:${(err as Error)?.message ?? String(err)}`);
      }

      return false;
    },
    [createIfaceHeaders, getDownloadedAppSettingDate]
  );

  const performActivationCheck = useCallback(
    (mode: ActivationCheckMode) => {
      if (activationCheckInFlightRef.current) {
        return activationCheckInFlightRef.current;
      }

      // eslint-disable-next-line complexity
      const checkPromise = (async () => {
        const isInitialCheck = mode === "initial";
        let didStartLoading = false;

        setActivationErrorCode(null);
        setErrorMessage(null);
        setReceptionErrorMessage(null);

        try {
          let activationData: ActivationCheckResponse;

          try {
            const response = await apiClient.get<{
              data: ActivationCheckResponse;
            }>(activationCheckUrl, {
              headers: createIfaceHeaders(true),
            });
            activationData = response.data.data;
            setActivationInfo(activationData);
          } catch (error) {
            const rawErrorCode = (
              error as {
                response?: { data?: { errorCode?: number | string } };
              }
            )?.response?.data?.errorCode;
            const errorCode =
              typeof rawErrorCode === "number"
                ? rawErrorCode
                : typeof rawErrorCode === "string" && rawErrorCode.trim() !== ""
                  ? Number(rawErrorCode)
                  : null;

            if (isInitialCheck) {
              setActivationErrorCode(Number.isFinite(errorCode) ? errorCode : null);
            }

            const modalMessageKey = getReceptionModalMessageKey(errorCode);

            if (modalMessageKey) {
              setIsExpired(errorCode === 103);

              if (isInitialCheck) {
                setActivationDataStatus("error");
              }

              setReceptionErrorMessage(t(modalMessageKey));
              stopPolling();

              return false;
            }

            throw error;
          }

          setIsExpired(false);

          // IndexedDB の activation ストアだけが失われた場合に備え、activation/check 応答から復元する。
          // timezone を含む後続処理より前に行うことで、復元した timezone を即座に利用できる。
          await restoreActivationDataIfMissing(activationData);

          // activation/check の maxUser / expireDate に差分があれば ActivationData を更新する
          // （SetActivation 画面と同じ条件）。
          await syncActivationDataFromCheck(activationData);

          const now = new Date();

          let isNeedUpdateAppSetting = false;
          let isNeedUpdatePhoneBook = false;
          let formattedPhoneBookStartDateTime = "";

          if (activationData.appSettingUpdateDateTime) {
            const reservationDate = new Date(activationData.appSettingUpdateDateTime);
            if (!Number.isNaN(reservationDate.getTime())) {
              const storedActivation = await getActivationData();
              const activationTimeZone = storedActivation?.timezone;
              const formattedReservationDateTime = activationTimeZone
                ? formatIsoDateTimeByTimeZone(
                    activationData.appSettingUpdateDateTime,
                    activationTimeZone
                  )
                : "";
              // 最終ダウンロード時刻（ISO 文字列または既存の保存値）を読み取る
              const storedAppSettingAuto = await getAppSettingAutoUpdateData();
              const storedAppSettingDate = storedAppSettingAuto?.lastUpdateDate ?? null;

              // activation/check が異なる値を返した場合に appSetting の自動更新時刻を保存する
              try {
                const formattedForSave =
                  formattedReservationDateTime || activationData.appSettingUpdateDateTime || null;
                if (formattedForSave && storedAppSettingAuto?.updateDate !== formattedForSave) {
                  await saveAppSettingAutoUpdateData(formattedForSave);
                }
              } catch (e) {
                console.error("Failed to sync appSettingAutoUpdate from activation/check:", e);
              }

              // ダウンロード要否判定: 最終ダウンロード時刻と予約時刻（ISO）を比較する
              if (storedAppSettingDate) {
                const lastMs = new Date(storedAppSettingDate).getTime();
                const schedMs = reservationDate.getTime();
                if (!Number.isNaN(lastMs) && !Number.isNaN(schedMs)) {
                  isNeedUpdateAppSetting =
                    lastMs < schedMs && reservationDate.getTime() < now.getTime();
                } else {
                  isNeedUpdateAppSetting = reservationDate.getTime() < now.getTime();
                }
              } else {
                isNeedUpdateAppSetting = reservationDate.getTime() < now.getTime();
              }
            }
          }
          // phoneBook 更新要否判定
          if (activationData.phoneBookId !== null && activationData.phoneBookId !== undefined) {
            const storedPhoneBook = await getStoredPhoneBook();
            const storedPhoneBookId = await getDownloadedPhoneBookId();
            const appPhoneBookId = storedPhoneBookId ?? 0;
            const hasStoredPhoneBookId = storedPhoneBookId !== null;

            const phoneBookStartDateTime = activationData.phoneBookStartDateTime;
            const storedActivation = await getActivationData();
            const activationTimeZone = storedActivation?.timezone;
            // activation のタイムゾーンが利用可能であればそのタイムゾーンで書式化する
            // 利用できない場合は ISO UTC 文字列にフォールバックして予約時刻を記録する
            if (phoneBookStartDateTime) {
              if (activationTimeZone) {
                formattedPhoneBookStartDateTime = formatIsoDateTimeByTimeZone(
                  phoneBookStartDateTime,
                  activationTimeZone
                );
              } else {
                const d = new Date(phoneBookStartDateTime);
                formattedPhoneBookStartDateTime = Number.isNaN(d.getTime()) ? "" : d.toISOString();
              }
            } else {
              formattedPhoneBookStartDateTime = "";
            }

            try {
              const storedPhoneBookAuto = await getPhoneBookAutoUpdateData();
              if (
                formattedPhoneBookStartDateTime &&
                storedPhoneBookAuto?.updateDate !== formattedPhoneBookStartDateTime
              ) {
                // start_date_time の変更を永続化して UI / 設定に反映させる
                await savePhoneBookAutoUpdateData(formattedPhoneBookStartDateTime);
              }
            } catch (e) {
              console.error("Failed to sync phoneBookAutoUpdate from activation/check:", e);
            }

            isNeedUpdatePhoneBook =
              isNeedUpdatePhoneBook ||
              appPhoneBookId < activationData.phoneBookId ||
              (storedPhoneBook === null && hasStoredPhoneBookId) ||
              (storedPhoneBook === null && !hasStoredPhoneBookId);
          }

          // appSettings は IndexedDB から参照する
          const storedAppSettings = await getAppSettingsData();
          const shouldDownloadAppSettings = isNeedUpdateAppSetting || !storedAppSettings;
          const shouldShowLoading = isNeedUpdatePhoneBook || shouldDownloadAppSettings;

          if (shouldShowLoading) {
            didStartLoading = true;
            setIsLoading(true);

            if (isInitialCheck) {
              setActivationDataStatus("loading");
            }
          }

          if (isNeedUpdatePhoneBook) {
            const isPhoneBookDownloaded = await downloadPhoneBookIfNeeded(activationData);
            if (isPhoneBookDownloaded && formattedPhoneBookStartDateTime) {
              await savePhoneBookAutoUpdateData(formattedPhoneBookStartDateTime);
              try {
                await touchPhoneBookAutoUpdateNow();
              } catch (e) {
                console.error("Failed to touch phoneBook auto update time:", e);
              }
            }
          }

          if (shouldDownloadAppSettings) {
            const appSettingUpdateDateTime = activationData.appSettingUpdateDateTime;
            const storedActivation = await getActivationData();
            const activationTimeZone = storedActivation?.timezone;
            const formattedAppSettingUpdateDateTime =
              appSettingUpdateDateTime && activationTimeZone
                ? formatIsoDateTimeByTimeZone(appSettingUpdateDateTime, activationTimeZone)
                : "";

            const isAppSettingsDownloaded = await downloadAppSettingsIfNeeded(activationData);
            if (isAppSettingsDownloaded && formattedAppSettingUpdateDateTime) {
              await saveAppSettingAutoUpdateData(formattedAppSettingUpdateDateTime);
              try {
                await touchAppSettingAutoUpdateNow();
              } catch (e) {
                console.error("Failed to touch appSetting auto update time:", e);
              }
            }
          }

          // ---- すべて成功 ----
          if (isInitialCheck) {
            setActivationDataStatus("success");
          }

          return true;
        } catch (error) {
          console.error("待受画面 activationチェック失敗:", error);
          if (isInitialCheck) {
            setActivationDataStatus("error");
          }
          setErrorMessage(t("systemStartupError"));

          return false;
        } finally {
          if (didStartLoading) {
            setIsLoading(false);
          }
        }
      })();

      activationCheckInFlightRef.current = checkPromise;

      return checkPromise.finally(() => {
        activationCheckInFlightRef.current = null;
      });
    },
    [downloadPhoneBookIfNeeded, downloadAppSettingsIfNeeded, stopPolling, t]
  );

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      void performActivationCheckRef.current?.("polling");
    }, ACTIVATION_CHECK_INTERVAL);
  }, []);

  useEffect(() => {
    performActivationCheckRef.current = performActivationCheck;
  }, [performActivationCheck]);

  /**
   * 初回チェックおよび1分間隔のポーリング開始
   */
  useEffect(() => {
    let isUnmounted = false;

    const init = async () => {
      const fn = performActivationCheckRef.current ?? performActivationCheck;
      const isInitialSuccess = await fn("initial");
      if (!isUnmounted && isInitialSuccess) {
        startPolling();
      }
    };

    performActivationCheckRef.current = performActivationCheck;
    void init();

    return () => {
      isUnmounted = true;
      stopPolling();
    };
  }, []);

  // 画面遷移を許可する前に電話帳メタがローカルに存在することを確認する
  const canNavigate =
    activationDataStatus === "success" && !isLoading && !isExpired && phoneBookMeta !== null;

  return {
    isLoading,
    isExpired,
    activationErrorCode,
    errorMessage,
    receptionErrorMessage,
    activationInfo,
    canNavigate,
    activationDataStatus,
    stopPolling,
    phoneBookMeta,
  };
};
